import { Button, Progress, Skeleton } from '@mantine/core';
import { IconArrowRight, IconGasStation, IconRoute } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './ActiveVoyagesPanel.module.css';
import { type VoyageCard, voyageCardOf } from './fuel-analysis.js';
import { useNumber } from './numbers.js';
import { useReportLabels } from './report-labels.js';
import type { VoyageSource } from './source.js';
import type { VoyageReport } from './types.js';
import { VOYAGE_TONE } from './VoyageFrame.js';

/** Panonun bir kartı: geminin adı (ofiste) ve seferin özeti. */
export interface ActiveVoyageItem {
  vessel: { id: string; name: string };
  card: VoyageCard;
}

export type ActiveVoyagesState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; items: ActiveVoyageItem[] };

/**
 * Süren seferler: her geminin "active" seferi ve raporları (All Reports ucu; yoksa seferin
 * raporları). Okunamayan gemi atlanır (pano düşmez); hiçbiri okunamazsa hata.
 */
export async function loadActiveVoyages(source: VoyageSource): Promise<ActiveVoyageItem[]> {
  const vessels = await source.vessels();
  const per = await Promise.allSettled(
    vessels.map(async (vessel) => {
      const voyages = (await source.voyages(vessel.id)).filter((v) => v.status === 'active');
      if (voyages.length === 0) return [];
      const reports: VoyageReport[] = source.allReports
        ? await source.allReports(vessel.id)
        : (await Promise.all(voyages.map((v) => source.reports(v.id)))).flat();
      return voyages.map((voyage) => ({
        vessel: { id: vessel.id, name: vessel.name },
        card: voyageCardOf(voyage, reports),
      }));
    }),
  );
  if (per.length > 0 && per.every((r) => r.status === 'rejected')) throw new Error('GENERIC');
  return per.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
}

/**
 * Pano ▸ Süren seferler (aşama V6; S19 eki; ADR 0090 §5): son rapor ve konumu, sonraki işlem ve
 * ETA, ilerleme, tüketim özeti ve ROB farkları; Voyage Details ve Fuel Analysis bağlantıları.
 * Ofiste filonun bütün süren seferleri, gemide geminin seferi. Raporlardan türetilir.
 */
export function ActiveVoyagesPanel({
  source,
  onNavigate,
}: {
  source: VoyageSource;
  onNavigate: (path: string) => void;
}) {
  const [state, setState] = useState<ActiveVoyagesState>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    loadActiveVoyages(source)
      .then((items) => live && setState({ kind: 'ready', items }))
      .catch(() => live && setState({ kind: 'error' }));
    return () => {
      live = false;
    };
  }, [source]);
  return <ActiveVoyagesView state={state} scope={source.scope} onNavigate={onNavigate} />;
}

/** Kartların gövdesi (veri dışarıdan; test edilebilir). */
export function ActiveVoyagesView({
  state,
  scope,
  onNavigate,
}: {
  state: ActiveVoyagesState;
  scope: 'web' | 'ship';
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.cards.${key}`);
  return (
    <Panel title={k('title')} icon={<IconRoute size={20} stroke={1.8} />}>
      {state.kind === 'loading' ? (
        <Skeleton height={150} radius="md" />
      ) : state.kind === 'error' ? (
        <p className={classes.muted}>{k('error')}</p>
      ) : state.items.length === 0 ? (
        // Seferi olmayan gemide (ör. yat) kart panoyu kalabalıklaştırmaz: tek satır
        <p className={classes.muted}>
          <strong>{k('none')}.</strong> {k('noneNote')}
        </p>
      ) : (
        <ul className={classes.grid}>
          {state.items.map((item) => (
            <VoyageCardItem
              key={item.card.voyage.id}
              item={item}
              scope={scope}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function VoyageCardItem({
  item,
  scope,
  onNavigate,
}: {
  item: ActiveVoyageItem;
  scope: 'web' | 'ship';
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const labels = useReportLabels();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.cards.${key}`, options);
  const { card, vessel } = item;
  const { voyage, progress, lastReport } = card;
  const query = `voyage=${voyage.id}${scope === 'web' ? `&vessel=${vessel.id}` : ''}`;
  const heading = scope === 'web' ? `${vessel.name} · ${voyage.voyageNo}` : voyage.voyageNo;
  return (
    <li className={classes.card} aria-label={heading}>
      <div className={classes.head}>
        <strong className={classes.title} title={heading}>
          {heading}
        </strong>
        <StatusBadge tone={VOYAGE_TONE[voyage.status]}>
          {t(`voyage.voyageStatus.${voyage.status}`)}
        </StatusBadge>
      </div>
      <p className={classes.route}>
        <span>{voyage.fromPort}</span>
        <IconArrowRight size={14} aria-hidden />
        <span>{voyage.toPort}</span>
      </p>
      <Progress
        value={progress.completedPct ?? 0}
        size="sm"
        radius="xl"
        aria-label={k('progress')}
      />
      <dl className={classes.facts}>
        <dt>{k('lastReport')}</dt>
        <dd>
          {lastReport
            ? `${labels.typeLabel(lastReport.kind)} · ${formatDateTime(lastReport.at, 0)}`
            : k('noReport')}
        </dd>
        {lastReport ? (
          <>
            <dt>{k('position')}</dt>
            <dd>{labels.locationLabel(lastReport.location)}</dd>
          </>
        ) : null}
        <dt>{k('next')}</dt>
        <dd>
          {card.nextOperation
            ? t(`voyage.sections.next.operations.${card.nextOperation}`)
            : k('notSet')}
        </dd>
        <dt>{k('eta', { port: voyage.toPort })}</dt>
        <dd>{formatDateTime(voyage.plannedEta, 0)}</dd>
        <dt>{k('consumption')}</dt>
        <dd>
          {card.totalMt === null
            ? '—'
            : k('consumptionValue', {
                total: n.unit(card.totalMt, 'MT', { digits: 1, fixed: true }),
                daily: n.unit(card.dailyMt, 'MT', { digits: 1, fixed: true }),
              })}
        </dd>
      </dl>
      <div className={classes.foot}>
        {card.robIssues > 0 ? (
          <StatusBadge tone="warning" withIcon>
            {k('robIssues', { count: card.robIssues })}
          </StatusBadge>
        ) : (
          <StatusBadge tone="success">{k('robOk')}</StatusBadge>
        )}
        <div className={classes.links}>
          <Button
            size="xs"
            variant="default"
            onClick={() => onNavigate(`/voyage/details?${query}`)}
          >
            {k('details')}
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconGasStation size={14} />}
            onClick={() => onNavigate(`/voyage/fuel-analysis?${query}`)}
          >
            {k('fuel')}
          </Button>
        </div>
      </div>
    </li>
  );
}
